import { ComponentType, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Table } from '@tanstack/react-table';
import { FloatingButton } from '@entur/button';
import { MinimizeIcon } from '@entur/icons';
import { useDrawer } from 'hooks/context/useDrawer';
import { Action, DrawerConfig, HighlightedItemType } from '../types';
import { useExpandedRows } from '../hooks/useExpandedRows';
import { type CleosTableFeaturesType } from '../tableFeatures';
import { TableRow } from './TableRow';
import styles from '../styles.module.scss';

interface TableBodyProps<TData extends object> {
  table: Table<CleosTableFeaturesType, TData>;
  expandable?: boolean;
  expandedContent?: ComponentType<{ content: TData; expandNextRow?: () => void }>;
  actions?: Action<TData>[];
  highlightedRow?: HighlightedItemType;
  hasColumnSelector?: boolean;
  tableKey?: string;
  canPersist?: boolean;
}

export const TableBody = <TData extends object>({
  table,
  expandable,
  expandedContent,
  actions,
  highlightedRow,
  hasColumnSelector,
  tableKey,
  canPersist,
}: TableBodyProps<TData>) => {
  const { open } = useDrawer();
  const { activeRows, toggleRow, expandNext, closeAll } = useExpandedRows(table, tableKey, canPersist);

  const rows = table.getRowModel().rows;

  const openDrawer = useCallback(
    (row: TData, drawer: DrawerConfig, index?: number) => {
      const r = row as Record<string, unknown>;
      const DrawerContent = drawer.content;
      if (drawer.multiple) {
        const ids = drawer.id ? (r[drawer.id] as unknown[]) : [];
        const altIds = drawer.altId ? (r[drawer.altId] as unknown[]) : [];
        open(<DrawerContent id={ids?.[index ?? 0]} altId={altIds?.[index ?? 0]} />);
      } else {
        open(
          <DrawerContent id={drawer.id ? r[drawer.id] : undefined} altId={drawer.altId ? r[drawer.altId] : undefined} />
        );
      }
    },
    [open]
  );

  return (
    <>
      <tbody className={styles.tbody}>
        {rows.map((row) => {
          // row.id is already derived from the caller's rowIdKey (see useTableInstance's
          // getRowId) — no need to recompute it here from row.original.
          const isExpanded = activeRows.includes(row.id);

          return (
            <TableRow
              key={row.id}
              row={row}
              expandable={expandable}
              expandedContent={expandedContent}
              isExpanded={isExpanded}
              onExpandToggle={() => toggleRow(row.id)}
              onExpandNext={() => expandNext(row.id)}
              actions={actions}
              highlightedRow={highlightedRow}
              hasColumnSelector={hasColumnSelector}
              openDrawer={openDrawer}
            />
          );
        })}
      </tbody>

      {activeRows.length > 0 &&
        createPortal(
          <FloatingButton
            id="closeAllExpandedLines"
            aria-label="Close all expanded rows"
            onClick={closeAll}
            style={{ position: 'fixed', bottom: '5px', right: '15px' }}
          >
            <MinimizeIcon />
          </FloatingButton>,
          document.body
        )}
    </>
  );
};
